import { Component, input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InsuranceClaimService } from '../../../../../../shared/store/insurance-claim/insurance-claim.service';
import { DocumentService } from '../../../../../../shared/store/document/document.service';
import { SubHeaderService } from '../../../../../../shared/store/subheader/subheader.service';
import { ConfigurationService } from '../../../../../../shared/service/configuration.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from '../../../../../../shared/service/modal.service';
import { FileSaverService } from 'ngx-filesaver';
import { combineLatest, Observable, of } from 'rxjs';
import { DocumentModel } from '../../../../../../shared/store/document/types';
import { InsuranceClaimModel } from '../../../../../../shared/store/insurance-claim/types';
import { filter, map, take } from 'rxjs/operators';
import { filterTrue } from '../../../../../../shared/pipe/rxjs/operators';
import { ModalContentComponent } from '../../../../../../shared/components/modal-content/modal-content.component';
import { ActionButton } from '../../../../../../shared/components/modal-content/action-button';
import { toFirstLetterLower } from '../../../../../../shared/helper/stringhelper';
import { SourceEnum } from '../../../../../../shared/store/typeahead/types';
import { ModalComponentKey } from 'src/app/shared/service/modal-component';

@UntilDestroy()
@Component({
  selector: 'app-insurance-claim-details-documents',
  standalone: false,
  templateUrl: './insurance-claim-details-documents.component.html',
  styleUrl: './insurance-claim-details-documents.component.scss',
})
export class InsuranceClaimDetailsDocumentsComponent
  implements OnInit, OnDestroy
{
  readonly insuranceClaimId = input.required<number>();
  readonly insuranceClaimUniqueId = input.required<string>();
  readonly contactId = input.required<number>();

  insuranceClaimDetail$: Observable<InsuranceClaimModel>;

  sortedDocuments$: Observable<DocumentModel[]>;

  contactDocuments$: Observable<DocumentModel[]>;
  insuranceClaimDocuments$: Observable<DocumentModel[]>;
  allDocuments$: Observable<DocumentModel[]>;

  copyToDocument$: Observable<DocumentModel>;

  isReadOnly$: Observable<boolean>;

  rowData: DocumentModel;
  downloadClicked: boolean = false;

  constructor(
    private readonly insuranceClaimService: InsuranceClaimService,
    private readonly documentService: DocumentService,
    private readonly subHeaderService: SubHeaderService,
    private readonly configurationService: ConfigurationService,
    private readonly modalService: NgbModal,
    private readonly modalExchangeService: ModalService,
    private readonly fileSaverService: FileSaverService,
  ) {
    this.insuranceClaimDetail$ =
      this.insuranceClaimService.getByUniqueId.value$;

    this.contactDocuments$ =
      this.documentService.getContactDocumentByEntityId.value$;
    this.insuranceClaimDocuments$ =
      this.documentService.getInsuranceClaimDocumentByEntityId.value$;
    this.copyToDocument$ = this.documentService.copyToDocumentRequest.value$;

    this.isReadOnly$ = of(false);
    // this.contractService.getIfDataReadOnlyFromContractId.value$;
  }

  ngOnInit(): void {
    this.documentService.getContactDocumentByEntityId.call({
      entityId: this.contactId(),
      entityType: 'Contact',
    });
    this.documentService.getInsuranceClaimDocumentByEntityId.call({
      entityId: this.insuranceClaimId(),
      entityType: 'InsuranceClaim',
    });
    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'documentDownload') {
          this.onClickDownload();
        }
        if (x == 'documentDelete') {
          this.onDelete();
        }
        if (x == 'documentEdit') {
          this.onOpenModalEdit();
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.sortedDocuments$ = this.insuranceClaimDetail$.pipe(
      map((model) => {
        return (
          [...(model.documents ?? [])]
            //Only take the request
            .filter((x) => !!x.requestDate)
            //Sort by mandatory and then by document type name
            .sort(
              (a, b) =>
                +!!b.mandatory - +!!a.mandatory ||
                (a.documentTypeName ?? '').localeCompare(
                  b.documentTypeName ?? '',
                ),
            )
        );
      }),
    );

    this.allDocuments$ = combineLatest([
      this.contactDocuments$,
      this.insuranceClaimDocuments$,
    ]).pipe(
      map(([contactDocumentModels, insuranceClaimDocumentModels]) => {
        let allDocumentModels: DocumentModel[] = [];

        if (contactDocumentModels && contactDocumentModels.length > 0) {
          allDocumentModels.push(...contactDocumentModels);
        }
        if (
          insuranceClaimDocumentModels &&
          insuranceClaimDocumentModels.length > 0
        ) {
          allDocumentModels.push(
            ...insuranceClaimDocumentModels.filter((x) => !x.requestDate),
          );
        }

        return allDocumentModels;
      }),
    );

    this.copyToDocument$
      .pipe(filterTrue(), untilDestroyed(this))
      .subscribe((document) => {
        if (document) {
          this.onRefreshInsuranceClaimDocument(true);
          this.onRefreshContactDocument(true);
          this.onNewDocument(true);
          this.documentService.copyToDocumentRequest.reset();
        }
      });
  }

  onNewDocument(success: boolean) {
    if (success) {
      this.insuranceClaimService.getByUniqueId.call(
        this.insuranceClaimUniqueId(),
      );
    }
  }

  onRefreshContactDocument(refresh: boolean) {
    if (refresh) {
      this.documentService.getContactDocumentByEntityId.call({
        entityType: 'Contact',
        entityId: this.contactId(),
      });
    }
  }

  onRefreshInsuranceClaimDocument(refresh: boolean) {
    if (refresh) {
      this.documentService.getInsuranceClaimDocumentByEntityId.call({
        entityType: 'InsuranceClaim',
        entityId: this.insuranceClaimId(),
      });
    }
  }

  onGetRowData() {
    //copy the data
    const result = this.rowData;

    //when we get the data, reset the data
    this.onSetRowData(undefined);

    //return the copy of the data
    return result;
  }

  onSetRowData(rowData: any) {
    this.rowData = rowData;
  }

  private onClickDownload(documentModel?: DocumentModel) {
    this.downloadClicked = true;
    const document: DocumentModel = documentModel ?? this.onGetRowData();

    const getFileSubscription = this.documentService.getFile.value$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result: Blob) => {
        if (
          this.configurationService
            .getOpenFileExtensions()
            .some((x) => x.includes(document.fileExtension))
        ) {
          const fileURL = window.URL.createObjectURL(result);
          let tab = window.open();
          if (tab) {
            tab.location.href = fileURL;
          }
        } else {
          this.fileSaverService.save(
            result,
            document.name,
            document.contentType,
          );
        }
        this.downloadClicked = false;
        // this.downloadFinished = true;
        this.documentService.getFile.reset();
      });

    this.documentService.getFile.call(document.id.toString());
  }

  private onDelete(documentModel?: DocumentModel) {
    const document: DocumentModel = documentModel ?? this.onGetRowData();

    // this.confirmationDialogService
    //   .confirm('Confirmation', 'Voulez-vous supprimer ?')
    //   .then((confirmed) => {
    //     if (confirmed) {
    //       let result$ = this.documentService.delete.call(
    //         document.id.toString(),
    //       );
    //
    //       result$.pipe(filterTrue(), take(1)).subscribe((result) => {
    //         this.documentService.delete.reset();
    //       });
    //     }
    //   })
    //   .catch(() => {});

    let result$ = this.documentService.delete.call(document.id.toString());

    result$.pipe(filterTrue(), take(1)).subscribe((result) => {
      //refresh data
      switch (document.entityType) {
        case 'Contact':
          this.onRefreshContactDocument(true);
          break;
        case 'InsuranceClaim':
          this.onRefreshInsuranceClaimDocument(true);
          break;
      }
      this.documentService.delete.reset();
    });
  }

  private onOpenModalEdit(documentModel?: DocumentModel) {
    if (this.downloadClicked) {
      this.downloadClicked = false;
      return;
    }
    const document: DocumentModel = documentModel ?? this.onGetRowData();

    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.DocumentEdit,
      );
    modalRef.componentInstance.title = 'Document';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('document') + 'Update',
      },
    ];
    modalRef.componentInstance.inputParams = {
      modalId: document.id,
      source: SourceEnum.Modal,
    };

    modalRef.result
      .then((id) => {
        if (id >= 0) {
          modalRef.close();

          //refresh data
          switch (document.entityType) {
            case 'Contact':
              this.onRefreshContactDocument(true);
              break;
            case 'InsuranceClaim':
              if (document.requestDate && document.fileExtension) {
                this.insuranceClaimService.getByUniqueId.call(
                  this.insuranceClaimUniqueId(),
                );
              } else {
                this.onRefreshInsuranceClaimDocument(true);
              }
              break;
          }
        }
      })
      .finally(() => {
        //avoid error on cancel
      });
  }

  onDocumentDownload(document: DocumentModel) {
    this.onClickDownload(document);
  }
  onDocumentEdit(document: DocumentModel) {
    this.onOpenModalEdit(document);
  }
  onDocumentDelete(document: DocumentModel) {
    let result$ = this.documentService.deleteFile.call(document.id.toString());

    result$.pipe(filterTrue(), take(1)).subscribe((result) => {
      //refresh data
      this.insuranceClaimService.getByUniqueId.call(
        this.insuranceClaimUniqueId(),
      );

      this.documentService.deleteFile.reset();
    });
  }

  ngOnDestroy(): void {
    this.documentService.deleteFile.reset();
    this.documentService.delete.reset();
    this.documentService.getContactDocumentByEntityId.reset();
    this.documentService.getInsuranceClaimDocumentByEntityId.reset();
  }
}
